// Training Load Calculation Utilities
// Risk band configuration - easily configurable constants
export const LOAD_RATIO_RISK_BANDS = {
  LOW_LOAD_RISK: { min: 0, max: 0.8, color: '#8B5CF6', label: 'Low-load Risk' }, // Purple
  LOW: { min: 0.8, max: 1.3, color: '#22C55E', label: 'Optimal Zone' }, // Green
  ELEVATED: { min: 1.3, max: 1.5, color: '#F59E0B', label: 'Elevated Risk' }, // Amber
  HIGH: { min: 1.5, max: Infinity, color: '#EF4444', label: 'High Risk' } // Red
};

// RPE intensity ranges configuration with colors
export const RPE_INTENSITY_RANGES = {
  LOW: {
    range: [1, 3],
    color: '#93C5FD', // light blue (blue-300)
    label: 'Low (RPE 1-3)'
  },
  MODERATE: {
    range: [4, 6],
    color: '#3B82F6', // medium blue (blue-500)
    label: 'Moderate (RPE 4-6)'
  },
  HIGH: {
    range: [7, 10],
    color: '#1E3A8A', // dark blue (blue-900)
    label: 'Hard (RPE 7-10)'
  }
};

// EWMA alpha calculation: alpha = 2 / (N + 1)
const ALPHA_ACUTE = 2 / (7 + 1);   // ~0.25 for 7-day effective window
const ALPHA_CHRONIC = 2 / (28 + 1); // ~0.069 for 28-day effective window

// Helper function to categorize RPE
const categorizeRPE = (rpe) => {
  if (rpe >= 1 && rpe <= 3) return 'LOW';
  if (rpe >= 4 && rpe <= 6) return 'MODERATE';
  if (rpe >= 7 && rpe <= 10) return 'HIGH';
  return 'MODERATE'; // Default fallback
};

// Helper function to calculate RPE breakdown for sessions on a specific date
const calculateRPEBreakdown = (dateSessions) => {
  const breakdown = {
    lowLoad: 0,      // RPE 1-3 (green)
    moderateLoad: 0, // RPE 4-6 (orange)
    highLoad: 0      // RPE 7-10 (red)
  };

  dateSessions?.forEach(session => {
    const category = categorizeRPE(session?.rpe);
    const sessionLoad = session?.load || (session?.minutes * session?.rpe);
    
    switch (category) {
      case 'LOW':
        breakdown.lowLoad += sessionLoad;
        break;
      case 'MODERATE':
        breakdown.moderateLoad += sessionLoad;
        break;
      case 'HIGH':
        breakdown.highLoad += sessionLoad;
        break;
    }
  });

  return breakdown;
};

/**
 * Calculate daily training loads from sessions
 * @param {Array} sessions - Array of training sessions
 * @returns {Object} - Daily loads keyed by date (YYYY-MM-DD)
 */
export const calculateDailyLoads = (sessions) => {
  const dailyLoads = {};
  
  sessions?.forEach(session => {
    const date = session?.date;
    if (!dailyLoads?.[date]) {
      dailyLoads[date] = 0;
    }
    // Sum all sessions on the same day (load = minutes × rpe)
    dailyLoads[date] += session?.load || (session?.minutes * session?.rpe);
  });
  
  return dailyLoads;
};

/**
 * Calculate EWMA (Exponentially Weighted Moving Average)
 * EWMA formula: ewma_t = alpha * load_t + (1 - alpha) * ewma_(t-1)
 * 
 * @param {Array} dailyLoads - Array of daily load values in chronological order
 * @param {number} alpha - Smoothing factor (2 / (N + 1) where N is effective window)
 * @param {number} windowSize - Nominal window size for initialization (7 or 28)
 * @returns {number} - EWMA value
 */
const calculateEWMA = (dailyLoads, alpha, windowSize) => {
  if (!dailyLoads || dailyLoads?.length === 0) return 0;

  // Initialization: Use mean of first windowSize days (or all available if less)
  const initWindow = dailyLoads?.slice(0, Math.min(windowSize, dailyLoads?.length));
  const initSum = initWindow?.reduce((sum, load) => sum + (load || 0), 0);
  let ewma = initSum / initWindow?.length;

  // If we have more data than the init window, calculate EWMA iteratively
  if (dailyLoads?.length > windowSize) {
    for (let i = windowSize; i < dailyLoads?.length; i++) {
      const currentLoad = dailyLoads?.[i] || 0;
      ewma = alpha * currentLoad + (1 - alpha) * ewma;
    }
  }

  return ewma;
};

/**
 * Calculate EWMA for a specific date with uncoupled acute/chronic windows
 * 
 * @param {Object} dailyLoadsMap - Daily loads keyed by date (YYYY-MM-DD)
 * @param {Date} targetDate - Target date for EWMA calculation
 * @param {number} alpha - Smoothing factor
 * @param {number} windowSize - Nominal window size for initialization
 * @returns {number} - EWMA value for the target date
 */
const calculateEWMAForDate = (dailyLoadsMap, targetDate, alpha, windowSize) => {
  // Get all dates up to and including target date
  const allDates = Object.keys(dailyLoadsMap)?.sort();
  const targetDateStr = targetDate?.toISOString()?.split('T')?.[0];
  const targetIndex = allDates?.indexOf(targetDateStr);
  
  if (targetIndex === -1) {
    // Target date not in data - get loads up to target date
    const loadsUpToTarget = [];
    for (let i = windowSize - 1; i >= 0; i--) {
      const checkDate = new Date(targetDate);
      checkDate?.setDate(checkDate?.getDate() - i);
      const dateStr = checkDate?.toISOString()?.split('T')?.[0];
      loadsUpToTarget?.push(dailyLoadsMap?.[dateStr] || 0);
    }
    return calculateEWMA(loadsUpToTarget, alpha, windowSize);
  }

  // Get loads from earliest date up to target date
  const relevantDates = allDates?.slice(0, targetIndex + 1);
  const loads = relevantDates?.map(date => dailyLoadsMap?.[date] || 0);

  return calculateEWMA(loads, alpha, windowSize);
};

/**
 * Generate comprehensive training load data for chart using EWMA-based ACWR
 * NOTE: This now uses EWMA instead of classic rolling average for ACWR calculation
 * 
 * @param {Array} sessions - All training sessions
 * @param {number} days - Number of days to generate (default 30)
 * @param {Date} endDate - End date for calculation (default today)
 * @returns {Array} - Array of chart data points with EWMA-based metrics
 */
export const generateTrainingLoadChartData = (sessions, days = 30, endDate = new Date()) => {
  if (!sessions || sessions?.length === 0) return [];

  const chartData = [];
  const end = new Date(endDate);
  end?.setHours(0, 0, 0, 0);

  // Pre-calculate all daily loads for EWMA calculations
  const allDailyLoads = calculateDailyLoads(sessions);

  // Generate data for each day in the range
  for (let i = days - 1; i >= 0; i--) {
    const currentDate = new Date(end);
    currentDate?.setDate(currentDate?.getDate() - i);
    currentDate?.setHours(0, 0, 0, 0);
    
    const dateStr = `${String(currentDate?.getDate())?.padStart(2, '0')}/${String(currentDate?.getMonth() + 1)?.padStart(2, '0')}`;
    const dateKey = currentDate?.toISOString()?.split('T')?.[0];

    // Get sessions for this specific date
    const dateSessions = sessions?.filter(session => {
      const sessionDate = new Date(session?.date);
      sessionDate?.setHours(0, 0, 0, 0);
      return sessionDate?.getTime() === currentDate?.getTime();
    });

    // Calculate daily total load and RPE breakdown
    const dailyLoad = allDailyLoads?.[dateKey] || 0;
    const rpeBreakdown = calculateRPEBreakdown(dateSessions);

    // Calculate EWMA-based acute load (7-day effective window)
    const acuteEWMA = calculateEWMAForDate(allDailyLoads, currentDate, ALPHA_ACUTE, 7);

    // Calculate EWMA-based chronic load (28-day effective window)
    const chronicEWMA = calculateEWMAForDate(allDailyLoads, currentDate, ALPHA_CHRONIC, 28);

    // Calculate load ratio (EWMA-based ACWR)
    let loadRatio = chronicEWMA > 0 ? acuteEWMA / chronicEWMA : null;

    chartData?.push({
      date: dateStr,
      dailyLoad: dailyLoad,
      lowLoad: rpeBreakdown?.lowLoad,
      moderateLoad: rpeBreakdown?.moderateLoad,
      highLoad: rpeBreakdown?.highLoad,
      acuteLoad: Math.round(acuteEWMA * 10) / 10,  // EWMA-based acute
      chronicLoad: Math.round(chronicEWMA * 10) / 10, // EWMA-based chronic
      loadRatio: loadRatio,
      displayRatio: loadRatio // For tooltip compatibility
    });
  }

  return chartData;
};

/**
 * Get risk category and color for a load ratio value
 * @param {number} ratio - Load ratio value
 * @returns {Object} - Risk band information
 */
export const getLoadRatioRiskBand = (ratio) => {
  if (!ratio || typeof ratio !== 'number') {
    return {
      category: 'insufficient_data',
      color: '#9CA3AF',
      label: 'Insufficient Data',
      tooltip: 'Insufficient chronic window data'
    };
  }
  
  for (const [key, band] of Object.entries(LOAD_RATIO_RISK_BANDS)) {
    if (ratio >= band?.min && ratio < band?.max) {
      return {
        category: key?.toLowerCase(),
        color: band?.color,
        label: band?.label,
        tooltip: `${band?.label}: ${ratio?.toFixed(2)}`
      };
    }
  }
  
  // Fallback to high risk
  return {
    category: 'high',
    color: LOAD_RATIO_RISK_BANDS?.HIGH?.color,
    label: LOAD_RATIO_RISK_BANDS?.HIGH?.label,
    tooltip: `${LOAD_RATIO_RISK_BANDS?.HIGH?.label}: ${ratio?.toFixed(2)}`
  };
};

/**
 * Calculate current training load statistics using EWMA-based ACWR
 * NOTE: This now uses EWMA instead of classic rolling average
 * 
 * @param {Array} sessions - All training sessions
 * @returns {Object} - Current load statistics with EWMA-based metrics
 */
export const calculateCurrentLoadStats = (sessions) => {
  const now = new Date();
  now?.setHours(0, 0, 0, 0);
  
  const dailyLoads = calculateDailyLoads(sessions);
  
  // Calculate EWMA-based acute load (7-day effective window)
  const acuteEWMA = calculateEWMAForDate(dailyLoads, now, ALPHA_ACUTE, 7);
  
  // Calculate EWMA-based chronic load (28-day effective window)
  const chronicEWMA = calculateEWMAForDate(dailyLoads, now, ALPHA_CHRONIC, 28);
  
  let loadRatio = null;
  if (chronicEWMA > 0) {
    loadRatio = acuteEWMA / chronicEWMA;
  }
  
  return {
    acuteLoad: Math.round(acuteEWMA * 10) / 10,
    chronicLoad: Math.round(chronicEWMA * 10) / 10,
    loadRatio: loadRatio ? Math.round(loadRatio * 100) / 100 : null,
    riskBand: getLoadRatioRiskBand(loadRatio)
  };
};

/**
 * Calculate load ratio trend and generate qualitative summary using EWMA-based ACWR
 * NOTE: This now uses EWMA instead of classic rolling average
 * 
 * @param {Array} sessions - All training sessions
 * @returns {Object} - Trend analysis with qualitative summary based on EWMA
 */
export const calculateLoadRatioTrend = (sessions) => {
  if (!sessions || sessions?.length === 0) {
    return {
      dailyChange: null,
      dailyChangePercent: null,
      sevenDayTrend: 'insufficient_data',
      qualitativeSummary: 'Insufficient data to calculate load ratio trends. Log more training sessions to see personalized insights.'
    };
  }

  const now = new Date();
  now?.setHours(0, 0, 0, 0);
  
  const dailyLoads = calculateDailyLoads(sessions);
  
  // Calculate EWMA-based load ratios for the last 8 days (today + 7 days back)
  const loadRatios = [];
  for (let i = 7; i >= 0; i--) {
    const date = new Date(now);
    date?.setDate(date?.getDate() - i);
    
    // Calculate EWMA-based acute and chronic loads for this date
    const acuteEWMA = calculateEWMAForDate(dailyLoads, date, ALPHA_ACUTE, 7);
    const chronicEWMA = calculateEWMAForDate(dailyLoads, date, ALPHA_CHRONIC, 28);
    
    const ratio = chronicEWMA > 0 ? acuteEWMA / chronicEWMA : null;
    loadRatios?.push(ratio);
  }

  // Current load ratio (today - index 7) and yesterday (index 6)
  const currentRatio = loadRatios?.[7];
  const yesterdayRatio = loadRatios?.[6];

  // Calculate daily change
  let dailyChange = null;
  let dailyChangePercent = null;
  if (currentRatio !== null && yesterdayRatio !== null && yesterdayRatio !== 0) {
    dailyChange = currentRatio - yesterdayRatio;
    dailyChangePercent = ((dailyChange / yesterdayRatio) * 100);
  }

  // Calculate 7-day trend
  let sevenDayTrend = 'stable';
  const validRatios = loadRatios?.filter(r => r !== null);
  
  if (validRatios?.length >= 3) {
    // Simple trend calculation: compare first half average to second half average
    const firstHalf = validRatios?.slice(0, Math.floor(validRatios?.length / 2));
    const secondHalf = validRatios?.slice(Math.floor(validRatios?.length / 2));
    
    const firstAvg = firstHalf?.reduce((sum, val) => sum + val, 0) / firstHalf?.length;
    const secondAvg = secondHalf?.reduce((sum, val) => sum + val, 0) / secondHalf?.length;
    
    const trendDiff = secondAvg - firstAvg;
    
    if (trendDiff > 0.05) sevenDayTrend = 'increasing';
    else if (trendDiff < -0.05) sevenDayTrend = 'decreasing';
    else sevenDayTrend = 'stable';
  }

  // Generate qualitative summary
  const qualitativeSummary = generateLoadRatioSummary(
    currentRatio,
    dailyChange,
    dailyChangePercent,
    sevenDayTrend
  );

  return {
    dailyChange: dailyChange ? Math.round(dailyChange * 100) / 100 : null,
    dailyChangePercent: dailyChangePercent ? Math.round(dailyChangePercent * 10) / 10 : null,
    sevenDayTrend: sevenDayTrend,
    qualitativeSummary: qualitativeSummary
  };
};

/**
 * Generate human-readable qualitative summary for load ratio
 * @param {number} currentRatio - Current load ratio
 * @param {number} dailyChange - Change from previous day
 * @param {number} dailyChangePercent - Percentage change from previous day
 * @param {string} sevenDayTrend - 7-day trend (increasing/decreasing/stable)
 * @returns {string} - Qualitative summary
 */
const generateLoadRatioSummary = (currentRatio, dailyChange, dailyChangePercent, sevenDayTrend) => {
  if (currentRatio === null) {
    return 'Insufficient chronic window data to calculate load ratio. Continue logging training sessions to build your baseline.';
  }

  const riskBand = getLoadRatioRiskBand(currentRatio);
  let summary = '';

  // Daily change commentary
  if (dailyChange !== null && Math.abs(dailyChange) > 0.01) {
    const changeDirection = dailyChange > 0 ? 'increased' : 'decreased';
    const changeAmount = Math.abs(dailyChangePercent)?.toFixed(1);
    summary += `Your load ratio has ${changeDirection} by ${changeAmount}% from yesterday. `;
  } else {
    summary += 'Your load ratio is similar to yesterday. ';
  }

  // 7-day trend commentary
  if (sevenDayTrend === 'increasing') {
    summary += 'Over the past 7 days, your training load has been trending upward, indicating progressive overload. ';
    if (riskBand?.category === 'elevated' || riskBand?.category === 'high') {
      summary += '⚠️ Consider moderating intensity to avoid overtraining.';
    } else if (riskBand?.category === 'low' || riskBand?.category === 'low_load_risk') {
      summary += '✅ This gradual increase is ideal for building fitness safely.';
    }
  } else if (sevenDayTrend === 'decreasing') {
    summary += 'Over the past 7 days, your training load has been decreasing, suggesting recovery or deload. ';
    if (riskBand?.category === 'low_load_risk') {
      summary += '⚠️ Extended low load may lead to deconditioning—consider increasing training volume.';
    } else {
      summary += '✅ This recovery period can help prevent injury and improve adaptation.';
    }
  } else {
    summary += 'Your training load has been relatively stable over the past 7 days. ';
    if (riskBand?.category === 'low' || riskBand?.category === 'low_load_risk') {
      summary += '✅ Maintaining this consistency supports steady fitness gains.';
    } else if (riskBand?.category === 'elevated' || riskBand?.category === 'high') {
      summary += '⚠️ Consider reducing load to return to the optimal zone.';
    }
  }

  return summary;
};

/**
 * Calculate rolling baseline statistics (mean and SD) over last 28 days
 * @param {Array} values - Array of numerical values
 * @returns {Object} - { mean, sd }
 */
const calculateRollingBaseline = (values) => {
  if (!values || values?.length === 0) {
    return { mean: null, sd: null };
  }

  const validValues = values?.filter(v => v !== null && v !== undefined && !isNaN(v));
  
  if (validValues?.length === 0) {
    return { mean: null, sd: null };
  }

  const mean = validValues?.reduce((sum, val) => sum + val, 0) / validValues?.length;
  
  if (validValues?.length === 1) {
    return { mean, sd: 0 };
  }

  const variance = validValues?.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / validValues?.length;
  const sd = Math.sqrt(variance);

  return { mean, sd };
};

/**
 * Calculate z-score with safe handling of zero SD
 * @param {number} value - Current value
 * @param {number} mean - Baseline mean
 * @param {number} sd - Baseline standard deviation
 * @returns {number} - Z-score (0 if SD is zero or undefined)
 */
const calculateZScore = (value, mean, sd) => {
  if (mean === null || sd === null || sd === 0) {
    return 0; // Default to "typical" when baseline unavailable
  }
  return (value - mean) / sd;
};

/**
 * Clip z-score to range [-2, +2]
 * @param {number} zScore - Z-score to clip
 * @returns {number} - Clipped z-score
 */
const clipZScore = (zScore) => {
  return Math.max(-2, Math.min(2, zScore));
};

/**
 * Rescale clipped z-score to [0, 1] risk component
 * @param {number} clippedZ - Clipped z-score
 * @returns {number} - Risk component (0 = no concern, 1 = highest concern)
 */
const rescaleToRiskComponent = (clippedZ) => {
  return (clippedZ + 2) / 4;
};

/**
 * Classify ACWR state based on absolute ACWR and z-score
 * @param {number} acwrEwma - Current ACWR EWMA value
 * @param {number} zR - ACWR z-score
 * @returns {string} - One of "low" | "moderate" | "high_spike"
 */
const classifyACWRState = (acwrEwma, zR) => {
  if (acwrEwma < 0.8 || zR <= -1.0) {
    return 'low';
  } else if (acwrEwma > 1.5 || zR >= 1.0) {
    return 'high_spike';
  } else {
    return 'moderate';
  }
};

/**
 * Classify wellness state based on Hooper z-score
 * @param {number} zHI - Hooper Index z-score
 * @returns {string} - One of "good" | "typical" | "suppressed" | "very_poor"
 */
const classifyWellnessState = (zHI) => {
  if (zHI <= -0.5) {
    return 'good';
  } else if (zHI < 0.5) {
    return 'typical';
  } else if (zHI < 1.5) {
    return 'suppressed';
  } else {
    return 'very_poor';
  }
};

/**
 * Determine readiness band from composite readiness index
 * @param {number} readinessIndex - Composite readiness index (0-1)
 * @returns {string} - One of "green" | "amber" | "red"
 */
const classifyReadinessBand = (readinessIndex) => {
  if (readinessIndex < 0.33) {
    return 'green';
  } else if (readinessIndex < 0.66) {
    return 'amber';
  } else {
    return 'red';
  }
};

/**
 * Generate recommendation text based on ACWR state, wellness state, and readiness band
 * @param {string} acwrState - ACWR state classification
 * @param {string} wellnessState - Wellness state classification
 * @param {string} readinessBand - Readiness band (green/amber/red)
 * @returns {string} - Recommendation text
 */
const generateReadinessRecommendation = (acwrState, wellnessState, readinessBand) => {
  // Case 6: Low ACWR + Poor Wellness
  if (acwrState === 'low' && (wellnessState === 'suppressed' || wellnessState === 'very_poor')) {
    return "Load is lower than usual but wellness is still poor. Treat this as possible illness or high life stress. Avoid hard sessions until wellness improves. Communicate with your coach and let them know your current Readiness Index, Training Load Ratio and Wellness Score.";
  }
  
  // Case 5: High spike ACWR + Poor Wellness
  if (acwrState === 'high_spike' && (wellnessState === 'suppressed' || wellnessState === 'very_poor')) {
    return "You are training harder than usual while feeling worse than normal. Strongly consider reducing tomorrow's load and emphasise recovery. Communicate with your coach and let them know your current Readiness Index, Training Load Ratio and Wellness Score.";
  }
  
  // Case 4: Moderate ACWR + Poor Wellness
  if (acwrState === 'moderate' && (wellnessState === 'suppressed' || wellnessState === 'very_poor')) {
    return "Your training load is typical, but you are reporting lower wellness than normal. Consider reducing the intensity or volume tomorrow and prioritise recovery. Communicate with your coach and let them know your current Readiness Index, Training Load Ratio and Wellness Score.";
  }
  
  // Case 3: High spike ACWR + Good/Typical Wellness
  if (acwrState === 'high_spike' && (wellnessState === 'good' || wellnessState === 'typical')) {
    return "You are training harder than usual and feel OK. Avoid adding extra unplanned volume or intense efforts tomorrow. Communicate with your coach and let them know your current Readiness Index, Training Load Ratio and Wellness Score.";
  }
  
  // Case 2: Low ACWR + Good/Typical Wellness
  if (acwrState === 'low' && (wellnessState === 'good' || wellnessState === 'typical')) {
    return "This looks like a lighter period and you are feeling OK. Maintain quality but re-introduce load gradually if you are building towards a goal. Communicate with your coach and let them know your current Readiness Index, Training Load Ratio and Wellness Score.";
  }
  
  // Case 1: Green band, Moderate ACWR, Good/Typical Wellness (default)
  return "Load and wellness are in line with your normal. Proceed with the planned session. Communicate with your coach and let them know your current Readiness Index, Training Load Ratio and Wellness Score.";
};

/**
 * Calculate Readiness Index for today based on 28-day rolling window
 * Combines EWMA-based ACWR and Hooper Index (wellness score)
 * 
 * @param {Array} sessions - All training sessions (must have date, load properties)
 * @param {Array} wellnessEntries - All wellness entries (must have date, hooper_index properties)
 * @returns {Object} - Readiness Index data with all calculations and recommendation
 */
export const calculateReadinessIndex = (sessions, wellnessEntries) => {
  const today = new Date();
  today?.setHours(0, 0, 0, 0);
  const todayStr = today?.toISOString()?.split('T')?.[0];

  // Get last 28 days of data (including today)
  const last28Days = [];
  for (let i = 27; i >= 0; i--) {
    const date = new Date(today);
    date?.setDate(date?.getDate() - i);
    last28Days?.push(date?.toISOString()?.split('T')?.[0]);
  }

  // Extract ACWR EWMA values for last 28 days
  const acwrValues = [];
  const dailyLoads = calculateDailyLoads(sessions);
  
  for (const dateStr of last28Days) {
    const date = new Date(dateStr);
    const acuteEWMA = calculateEWMAForDate(dailyLoads, date, ALPHA_ACUTE, 7);
    const chronicEWMA = calculateEWMAForDate(dailyLoads, date, ALPHA_CHRONIC, 28);
    const acwrEwma = chronicEWMA > 0 ? acuteEWMA / chronicEWMA : null;
    acwrValues?.push(acwrEwma);
  }

  // Extract Hooper Index values for last 28 days
  const hooperValues = [];
  const wellnessMap = {};
  wellnessEntries?.forEach(entry => {
    wellnessMap[entry?.date] = entry?.hooper_index || (entry?.sleep + entry?.fatigue + entry?.soreness + entry?.stress);
  });
  
  for (const dateStr of last28Days) {
    hooperValues?.push(wellnessMap?.[dateStr] || null);
  }

  // Get today's values
  const todayACWR = acwrValues?.[acwrValues?.length - 1];
  const todayHooper = hooperValues?.[hooperValues?.length - 1];

  // Check if we have sufficient data
  if (todayACWR === null || todayHooper === null) {
    return {
      readinessIndex: null,
      readinessBand: 'insufficient_data',
      recommendation: todayACWR === null && todayHooper === null
        ? 'Insufficient data: Please log training sessions and complete wellness entries to calculate your Readiness Index.'
        : todayACWR === null
        ? 'Insufficient training data: Log more training sessions to build your baseline for Readiness Index.'
        : 'Wellness data missing: Complete today\'s wellness entry to calculate your Readiness Index.',
      components: null,
      states: null
    };
  }

  // Calculate rolling baseline statistics
  const hooperBaseline = calculateRollingBaseline(hooperValues);
  const acwrBaseline = calculateRollingBaseline(acwrValues);

  // Calculate z-scores
  const zHI = calculateZScore(todayHooper, hooperBaseline?.mean, hooperBaseline?.sd);
  const zR = calculateZScore(todayACWR, acwrBaseline?.mean, acwrBaseline?.sd);

  // Clip z-scores
  const zHI_clipped = clipZScore(zHI);
  const zR_clipped = clipZScore(zR);

  // Rescale to risk components [0, 1]
  const rHI = rescaleToRiskComponent(zHI_clipped);
  const rR = rescaleToRiskComponent(zR_clipped);

  // Calculate composite readiness index
  const readinessIndex = 0.5 * rHI + 0.5 * rR;

  // Classify states
  const acwrState = classifyACWRState(todayACWR, zR);
  const wellnessState = classifyWellnessState(zHI);
  const readinessBand = classifyReadinessBand(readinessIndex);

  // Generate recommendation
  const recommendation = generateReadinessRecommendation(acwrState, wellnessState, readinessBand);

  return {
    readinessIndex: Math.round(readinessIndex * 100) / 100, // Round to 2 decimals
    readinessBand: readinessBand,
    recommendation: recommendation,
    components: {
      hooperRisk: Math.round(rHI * 100) / 100,
      acwrRisk: Math.round(rR * 100) / 100,
      zScores: {
        hooper: Math.round(zHI * 100) / 100,
        acwr: Math.round(zR * 100) / 100
      }
    },
    states: {
      acwrState: acwrState,
      wellnessState: wellnessState
    },
    todayValues: {
      acwr: Math.round(todayACWR * 100) / 100,
      hooperIndex: todayHooper
    }
  };
};

/**
 * Calculate Readiness Index for each day in a date range for chart visualization
 * @param {Array} sessions - All training sessions
 * @param {Array} wellnessEntries - All wellness entries
 * @param {Array} dates - Array of date strings to calculate readiness for
 * @returns {Array} - Array of objects with date and readinessIndex
 */
export const calculateReadinessForDateRange = (sessions, wellnessEntries, dates) => {
  const dailyLoads = calculateDailyLoads(sessions);
  const wellnessMap = {};
  
  wellnessEntries?.forEach(entry => {
    wellnessMap[entry?.date] = entry?.hooper_index || (entry?.sleep + entry?.fatigue + entry?.soreness + entry?.stress);
  });

  return dates?.map(dateStr => {
    const targetDate = new Date(dateStr);
    targetDate?.setHours(0, 0, 0, 0);

    // Get last 28 days of data (including target date)
    const last28Days = [];
    for (let i = 27; i >= 0; i--) {
      const date = new Date(targetDate);
      date?.setDate(date?.getDate() - i);
      last28Days?.push(date?.toISOString()?.split('T')?.[0]);
    }

    // Extract ACWR EWMA values for last 28 days
    const acwrValues = [];
    for (const date of last28Days) {
      const dateObj = new Date(date);
      const acuteEWMA = calculateEWMAForDate(dailyLoads, dateObj, ALPHA_ACUTE, 7);
      const chronicEWMA = calculateEWMAForDate(dailyLoads, dateObj, ALPHA_CHRONIC, 28);
      const acwrEwma = chronicEWMA > 0 ? acuteEWMA / chronicEWMA : null;
      acwrValues?.push(acwrEwma);
    }

    // Extract Hooper Index values for last 28 days
    const hooperValues = [];
    for (const date of last28Days) {
      hooperValues?.push(wellnessMap?.[date] || null);
    }

    // Get target date's values
    const targetACWR = acwrValues?.[acwrValues?.length - 1];
    const targetHooper = hooperValues?.[hooperValues?.length - 1];

    // Check if we have sufficient data
    if (targetACWR === null || targetHooper === null) {
      return {
        date: dateStr,
        readinessIndex: null
      };
    }

    // Calculate rolling baseline statistics
    const hooperBaseline = calculateRollingBaseline(hooperValues);
    const acwrBaseline = calculateRollingBaseline(acwrValues);

    // Calculate z-scores
    const zHI = calculateZScore(targetHooper, hooperBaseline?.mean, hooperBaseline?.sd);
    const zR = calculateZScore(targetACWR, acwrBaseline?.mean, acwrBaseline?.sd);

    // Clip z-scores
    const zHI_clipped = clipZScore(zHI);
    const zR_clipped = clipZScore(zR);

    // Rescale to risk components [0, 1]
    const rHI = rescaleToRiskComponent(zHI_clipped);
    const rR = rescaleToRiskComponent(zR_clipped);

    // Calculate composite readiness index
    const readinessIndex = 0.5 * rHI + 0.5 * rR;

    return {
      date: dateStr,
      readinessIndex: Math.round(readinessIndex * 100) / 100
    };
  });
};